import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OrbitalAnimation from '../components/OrbitalAnimation';
import TaraButton from '../components/ui/TaraButton';
import { toast } from 'sonner';
import { generateTTSChunkTexts } from '../utils/storyTextProcessor';
import { saveToGlobalStore } from '../utils/globalAudioStore';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

const LoadingStages = [
  {
    title: 'Aligning your stars',
    description: 'Calculating your planetary positions from NASA ephemeris data',
    progress: [0, 15],
  },
  {
    title: 'Reading your cosmic blueprint',
    description: 'Analyzing 2,000+ astrological combinations in your chart',
    progress: [15, 30],
  },
  {
    title: 'Translating into your story',
    description: 'AI is weaving your personalized narrative',
    progress: [30, 40],
  },
  {
    title: 'Generating Hindi sage narration',
    description: '🎙️ Creating complete audio for हिंदी (this may take a few minutes)',
    progress: [40, 60],
  },
  {
    title: 'Generating Hinglish sage narration',
    description: '🎙️ Creating complete audio for Hinglish (please wait...)',
    progress: [60, 80],
  },
  {
    title: 'Generating English sage narration',
    description: '🎙️ Creating complete audio for English (almost done...)',
    progress: [80, 98],
  },
  {
    title: 'Your story is ready!',
    description: '✨ All narrations complete - tap to begin your journey',
    progress: [98, 100],
  },
];

// TTS Configuration - Using Edge TTS (FREE MadhurNeural)
const TTS_ENDPOINT = '/api/tts/edge';
const MAX_CHUNK_CHARS = 400;

// Split text into chunks at sentence boundaries
const splitTextIntoChunks = (text, maxChars = MAX_CHUNK_CHARS) => {
  if (!text || text.length <= maxChars) return text ? [text] : [];
  
  const chunks = [];
  let remaining = text;
  
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining.trim());
      break;
    }
    
    let breakPoint = maxChars;
    // Try to break at sentence boundaries
    for (const endChar of ['। ', '. ', '? ', '! ', '\n\n', '\n', ', ']) {
      const lastIndex = remaining.substring(0, maxChars).lastIndexOf(endChar);
      if (lastIndex > maxChars * 0.4) {
        breakPoint = lastIndex + endChar.length;
        break;
      }
    }
    
    chunks.push(remaining.substring(0, breakPoint).trim());
    remaining = remaining.substring(breakPoint).trim();
  }
  
  return chunks.filter(c => c.length > 10);
};

// Generate full story text for a specific language
const generateFullStoryText = (storyData, language, userName) => {
  if (!storyData) return '';
  
  const pastMds = storyData.mahadashas || [];
  let fullText = '';
  
  // Get intro for this specific language
  const intro = storyData.intro?.[language];
  if (intro) {
    fullText += intro + '\n\n';
  } else {
    // Generate language-specific fallback intro
    if (language === 'hindi') {
      fullText += `नमस्ते ${userName}! मैं आपका ज्ञानी गुरु हूं। आज मैं आपको आपके बीते हुए जीवन की कहानी सुनाऊंगा।\n\n`;
    } else if (language === 'english') {
      fullText += `Greetings ${userName}! I am your wise guru. Today I will narrate to you the story of your past life.\n\n`;
    } else {
      fullText += `Namaste ${userName}! Main aapka gyaani guru hoon. Aaj main aapko aapke beete huye jeevan ki kahani sunaunga.\n\n`;
    }
  }
  
  // Get timeline narrations for this specific language
  const timelineNarrations = storyData.timeline_narrations?.[language] || [];
  
  // Add each mahadasha narration
  pastMds.forEach((md, idx) => {
    if (timelineNarrations[idx]) {
      fullText += timelineNarrations[idx] + '\n\n';
    }
  });
  
  return fullText.trim();
};

const LoadingStory = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [audioStatus, setAudioStatus] = useState('');
  const [detailedStatus, setDetailedStatus] = useState('');
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [allAudioReady, setAllAudioReady] = useState(false);
  const [generationStats, setGenerationStats] = useState({ 
    hindi: { done: 0, total: 0 }, 
    hinglish: { done: 0, total: 0 }, 
    english: { done: 0, total: 0 } 
  });
  const [errorMessage, setErrorMessage] = useState('');
  const predictionId = localStorage.getItem('prediction_id');
  const audioGenerationStarted = useRef(false);
  const abortRef = useRef(false);
  
  // Generate TTS audio for a single chunk - WAIT for completion with retries
  // Saves to globalAudioStore so PastStory can read it instantly (0ms)
  const generateTTSAudio = async (text, language, retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      const response = await fetch(`${API_URL}${TTS_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: text.substring(0, 600),
          language: language 
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.audio) {
          // Save to globalAudioStore for PastStory instant pickup
          try {
            const byteChars = atob(result.audio);
            const byteArr = new Uint8Array(byteChars.length);
            for (let k = 0; k < byteChars.length; k++) byteArr[k] = byteChars.charCodeAt(k);
            const blob = new Blob([byteArr], { type: 'audio/mpeg' });
            const blobUrl = URL.createObjectURL(blob);
            const dataUrl = `data:audio/mpeg;base64,${result.audio}`;
            saveToGlobalStore(language, text, blobUrl, dataUrl);
          } catch (_) {}
          return { success: true, cached: result.cached, chars: result.chars_used || text.length };
        }
      }
      
      if (retryCount < maxRetries) {
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return generateTTSAudio(text, language, retryCount + 1);
      }
      
      return { success: false, cached: false, chars: 0 };
    } catch (e) {
      console.error(`TTS error for ${language}:`, e.message);
      if (retryCount < maxRetries) {
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return generateTTSAudio(text, language, retryCount + 1);
      }
      return { success: false, cached: false, chars: 0 };
    }
  };
  
  // PRE-GENERATE ALL AUDIO FOR ALL 3 LANGUAGES - COMPLETE GENERATION - NO SHORTCUTS
  const preGenerateAllAudio = async (storyData, userName) => {
    if (audioGenerationStarted.current) return;
    audioGenerationStarted.current = true;
    setIsGeneratingAudio(true);
    abortRef.current = false;
    setErrorMessage('');
    
    const languages = ['hindi', 'hinglish', 'english'];
    const languageLabels = { 
      hindi: 'हिंदी', 
      hinglish: 'Hinglish', 
      english: 'English' 
    };
    const stageMap = { hindi: 3, hinglish: 4, english: 5 };
    
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🎙️ STARTING COMPLETE AUDIO PRE-GENERATION FOR ALL 3 LANGUAGES');
    console.log('⏳ This will take several minutes - DO NOT close this page');
    console.log('═══════════════════════════════════════════════════════════');
    
    const userId = localStorage.getItem('session_id') || predictionId;
    
    // Get chunk texts using SAME shared function as PastStory
    // buildStoryData → splitTextIntoChunks = identical chunks = globalAudioStore cache HIT
    let totalChunks = 0;
    const chunksPerLang = {};
    
    for (const lang of languages) {
      const chunks = generateTTSChunkTexts(storyData, lang);
      chunksPerLang[lang] = chunks;
      totalChunks += chunks.length;
      
      setGenerationStats(prev => ({
        ...prev,
        [lang]: { done: 0, total: chunks.length }
      }));
    }
    
    console.log(`📊 TOTAL CHUNKS TO GENERATE: ${totalChunks}`);
    console.log('═══════════════════════════════════════════════════════════');
    
    let completedChunks = 0;
    let failedChunks = 0;
    let totalCharsGenerated = 0;
    const generatedAudioData = {};
    
    // Generate each language COMPLETELY before moving to next
    for (const lang of languages) {
      if (abortRef.current) break;
      
      setCurrentStage(stageMap[lang]);
      const chunks = chunksPerLang[lang];
      generatedAudioData[lang] = { 
        chunks: [], 
        totalChunks: chunks.length,
        generatedChunks: 0,
        failedChunks: 0
      };
      
      console.log(`\n🎙️ ═══ GENERATING ${lang.toUpperCase()} ═══`);
      console.log(`   Total chunks: ${chunks.length}`);
      
      for (let i = 0; i < chunks.length; i++) {
        if (abortRef.current) break;
        
        const chunkText = chunks[i];
        const chunkPreview = chunkText.substring(0, 30) + '...';
        
        // Update detailed status
        setAudioStatus(`${languageLabels[lang]}: ${i + 1}/${chunks.length}`);
        setDetailedStatus(`"${chunkPreview}"`);
        
        // Generate TTS and WAIT for it to complete (with retries built-in)
        const result = await generateTTSAudio(chunkText, lang);
        
        if (result.success) {
          generatedAudioData[lang].chunks.push({ 
            text: chunkText, 
            index: i,
            cached: result.cached
          });
          generatedAudioData[lang].generatedChunks = i + 1;
          totalCharsGenerated += result.chars;
          
          const status = result.cached ? '(cached)' : `(${result.chars} chars)`;
          console.log(`   ✅ ${lang} chunk ${i + 1}/${chunks.length} ${status}`);
        } else {
          failedChunks++;
          generatedAudioData[lang].failedChunks++;
          console.error(`   ❌ ${lang} chunk ${i + 1}/${chunks.length} FAILED after retries`);
        }
        
        completedChunks++;
        
        // Update stats for this language
        setGenerationStats(prev => ({
          ...prev,
          [lang]: { done: i + 1, total: chunks.length }
        }));
        
        // Calculate overall progress (40% to 98%)
        const audioProgress = 40 + Math.round((completedChunks / totalChunks) * 58);
        setProgress(audioProgress);
        
        // Small delay between chunks to prevent rate limiting
        if (!result.cached) {
          await new Promise(r => setTimeout(r, 150));
        }
      }
      
      console.log(`   ═══ ${lang.toUpperCase()} COMPLETE: ${generatedAudioData[lang].generatedChunks}/${chunks.length} chunks ═══`);
    }
    
    // Verify ALL languages are COMPLETE
    let allComplete = true;
    let verificationReport = '\n═══════════════════════════════════════════════════════════\n';
    verificationReport += '📋 VERIFICATION REPORT:\n';
    
    for (const lang of languages) {
      const data = generatedAudioData[lang];
      const success = data && data.generatedChunks === data.totalChunks;
      const status = success ? '✅' : '❌';
      verificationReport += `   ${status} ${lang.toUpperCase()}: ${data?.generatedChunks || 0}/${data?.totalChunks || 0} chunks\n`;
      if (!success) allComplete = false;
    }
    
    verificationReport += `   📊 Total generated: ${completedChunks - failedChunks}/${totalChunks}\n`;
    verificationReport += `   📊 Total chars: ${totalCharsGenerated}\n`;
    verificationReport += `   💰 Est. cost: $${((totalCharsGenerated / 1000000) * 16).toFixed(4)}\n`;
    verificationReport += '═══════════════════════════════════════════════════════════';
    console.log(verificationReport);
    
    if (!allComplete) {
      console.warn('⚠️ WARNING: Some audio generation was incomplete!');
      setErrorMessage(`Some audio chunks failed to generate. ${failedChunks} chunks failed.`);
    }
    
    // Store in localStorage for the story page
    localStorage.setItem('audio_pregenerated', 'true');
    localStorage.setItem('audio_user_id', userId);
    localStorage.setItem('audio_provider', 'google_wavenet');
    localStorage.setItem('pregenerated_audio_data', JSON.stringify(generatedAudioData));
    localStorage.setItem('audio_generation_complete', allComplete ? 'true' : 'partial');
    localStorage.setItem('audio_generation_timestamp', new Date().toISOString());
    
    setProgress(100);
    setCurrentStage(6);
    setAudioStatus('All narrations ready!');
    setDetailedStatus('');
    setIsGeneratingAudio(false);
    setAllAudioReady(true);
    
    // Show success message
    if (allComplete) {
      toast.success('Your complete story is ready with sage narration!');
    } else {
      toast.warning('Story ready, but some audio may be incomplete.');
    }
    
    // Auto-navigate after user sees the success state
    setTimeout(() => {
      navigate('/your-story');
    }, 2500);
  };
  
  useEffect(() => {
    if (!predictionId) {
      toast.error('No prediction ID found');
      navigate('/name-entry');
      return;
    }
    
    // Check if audio was already generated
    const audioComplete = localStorage.getItem('audio_generation_complete');
    if (audioComplete === 'true') {
      console.log('✅ Audio already pre-generated, navigating to story...');
      navigate('/your-story');
      return;
    }
    
    // Check if this is a demo/mock prediction ID
    const isDemoId = predictionId.startsWith('demo_');
    
    if (isDemoId) {
      // For demo IDs, simulate initial progress then generate audio
      let currentProgress = 0;
      const demoInterval = setInterval(() => {
        if (isGeneratingAudio) {
          return; // Don't update progress while generating audio
        }
        
        currentProgress += 2;
        setProgress(Math.min(currentProgress, 40)); // Cap at 40% before audio generation
        
        // Update stage based on progress
        if (currentProgress >= 30) setCurrentStage(2);
        else if (currentProgress >= 15) setCurrentStage(1);
        else setCurrentStage(0);
        
        // At 40%, start COMPLETE audio pre-generation
        if (currentProgress >= 40 && !audioGenerationStarted.current) {
          clearInterval(demoInterval);
          
          const storedData = localStorage.getItem('unified_story_data');
          if (storedData) {
            try {
              const unifiedData = JSON.parse(storedData);
              // Birth Details = Source of Truth for name
              const birthRaw2 = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
              const birthParsed2 = birthRaw2 ? JSON.parse(birthRaw2) : null;
              const userName = birthParsed2?.name || unifiedData.user_info?.name || 'User';
              const pastStory = unifiedData.past_story;
              
              if (pastStory && pastStory.intro && pastStory.timeline_narrations) {
                console.log('Story data found, starting audio generation...');
                preGenerateAllAudio(unifiedData, userName);
              } else {
                console.error('Invalid story data structure');
                setErrorMessage('Invalid story data. Please go back and try again.');
              }
            } catch (e) {
              console.error('Error parsing story data:', e);
              setErrorMessage('Error loading story data. Please refresh.');
            }
          } else {
            console.error('No story data found in localStorage');
            setErrorMessage('No story data found. Please go back and generate your story first.');
          }
        }
      }, 400);
      
      return () => clearInterval(demoInterval);
    }
    
    // Real backend flow - poll for status
    const pollInterval = setInterval(async () => {
      if (isGeneratingAudio) return;
      
      try {
        const response = await fetch(`${API_URL}/api/story/status/${predictionId}`);
        const data = await response.json();
        
        const storyProgress = Math.min(data.progress || 0, 40);
        setProgress(storyProgress);
        
        if (storyProgress >= 30) setCurrentStage(2);
        else if (storyProgress >= 15) setCurrentStage(1);
        else setCurrentStage(0);
        
        if (data.status === 'complete' || storyProgress >= 40) {
          clearInterval(pollInterval);
          
          const storedData = localStorage.getItem('unified_story_data');
          if (storedData) {
            try {
              const unifiedData = JSON.parse(storedData);
              const birthRaw2 = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
              const birthParsed2 = birthRaw2 ? JSON.parse(birthRaw2) : null;
              const userName = birthParsed2?.name || unifiedData.user_info?.name || 'User';
              const pastStory = unifiedData.past_story;
              
              if (pastStory && pastStory.intro && pastStory.timeline_narrations) {
                preGenerateAllAudio(unifiedData, userName);
              } else {
                setErrorMessage('Invalid story data structure.');
              }
            } catch (e) {
              setErrorMessage('Error loading story data.');
            }
          } else {
            setErrorMessage('No story data found.');
          }
        } else if (data.status === 'error') {
          clearInterval(pollInterval);
          setErrorMessage('Failed to generate story: ' + (data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Status poll error:', error);
      }
    }, 3000);
    
    return () => {
      clearInterval(pollInterval);
      abortRef.current = true;
    };
  }, [predictionId, navigate, isGeneratingAudio]);
  
  return (
    <div className="min-h-screen bg-cosmic-dusk flex flex-col items-center justify-center px-5 py-8">
      <div className="max-w-md w-full space-y-8">
        {/* Orbital Animation */}
        <div className="relative h-44 flex items-center justify-center">
          <OrbitalAnimation progress={progress} />
        </div>
        
        {/* Stage Info */}
        <div className="text-center space-y-3">
          <h2 className="font-serif text-2xl md:text-3xl font-medium text-stone-900">
            {LoadingStages[currentStage].title}
          </h2>
          <p className="text-sm text-stone-600 leading-relaxed px-4">
            {LoadingStages[currentStage].description}
          </p>
          
          {/* Error message */}
          {errorMessage && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errorMessage}</p>
              <button 
                onClick={() => navigate('/onboarding-new')}
                className="mt-2 text-sm text-red-700 underline"
              >
                Go back to start
              </button>
            </div>
          )}
          
          {/* Audio generation status */}
          {isGeneratingAudio && (
            <div className="mt-6 space-y-4">
              <p className="text-base font-semibold text-cosmic-700">
                🎙️ {audioStatus}
              </p>
              {detailedStatus && (
                <p className="text-xs text-stone-500 italic truncate max-w-xs mx-auto">
                  {detailedStatus}
                </p>
              )}
              
              {/* Language progress indicators */}
              <div className="space-y-3 px-4">
                {['hindi', 'hinglish', 'english'].map(lang => {
                  const stats = generationStats[lang];
                  const percent = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;
                  const label = lang === 'hindi' ? 'हिंदी' : lang === 'hinglish' ? 'Hinglish' : 'English';
                  
                  return (
                    <div key={lang} className="flex items-center gap-3">
                      <span className="text-xs text-stone-600 w-16">{label}</span>
                      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${
                            percent === 100 ? 'bg-green-500' : 'bg-cosmic-500'
                          }`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-xs text-stone-500 w-12 text-right">
                        {stats.done}/{stats.total}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              <p className="text-xs text-stone-500 mt-4">
                ⏳ Please wait while we prepare your complete story narration...
                <br />
                <span className="text-stone-400">This ensures instant playback on the story page.</span>
              </p>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="h-2.5 bg-stone-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cosmic-500 to-cosmic-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-center text-stone-600 font-medium">
            {progress}% complete
          </p>
        </div>
        
        {/* Manual Continue Button (only when all audio is ready) */}
        {allAudioReady && (
          <div className="pt-4">
            <TaraButton
              variant="sacred"
              size="lg"
              onClick={() => {
                navigate('/your-story');
              }}
              className="w-full"
            >
              Begin Your Story Journey →
            </TaraButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingStory;
